import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MDEditor from '@uiw/react-md-editor';
import ReactQuill from 'react-quill';
import TransparentButton from '@/trio-ui/TransparentButton';
import WarningMessage from '@/trio-ui/WarningMessage';
import * as Styles from './TextEditor.styles';
import 'react-quill/dist/quill.snow.css';

export type TextEditorProps = {
  errorMessage?: string;
  hideButtons?: boolean;
  hideEditButton?: boolean;
  onEditContent: () => void;
  onChangeValue: (event: string | undefined) => void;
  value: string;
  isEditing?: boolean;
  isDisabled?: boolean;
  onCancelContentEdition: () => void;
  onSaveContent: () => void;
  markdownClassNames?: string;
};

const modules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }, { align: [] }],
    ['link'],
    ['clean'],
  ],
};

export default function TextEditorComponent({
  isEditing,
  errorMessage,
  hideButtons,
  hideEditButton,
  onEditContent,
  onCancelContentEdition,
  onChangeValue,
  value,
  onSaveContent,
  markdownClassNames,
  ...props
}: TextEditorProps): JSX.Element {
  return (
    <Styles.Container
      isEditing={!!isEditing}
      hideEditButton={!!hideEditButton}
      className="markdown-editor"
      data-testid="text-editor"
      {...props}
    >
      {isEditing ? (
        <div className="editor-area" data-testid="editor-area">
          <ReactQuill
            value={value}
            onChange={(e) => onChangeValue(e)}
            modules={modules}
            preserveWhitespace
            placeholder="Type or paste your text here..."
          />
          {errorMessage && <WarningMessage errorMessage={errorMessage} />}
        </div>
      ) : (
        <Styles.EditorContent className="ql-editor" data-testid="editor-content">
          <MDEditor.Markdown className={markdownClassNames} source={value} />
        </Styles.EditorContent>
      )}

      {!hideEditButton && !isEditing && (
        <Styles.Footer>
          <TransparentButton
            icon={EditIcon}
            data-testid="text-editor-edit-btn"
            onClick={onEditContent}
          >
            Edit
          </TransparentButton>
        </Styles.Footer>
      )}

      {!hideButtons && (isEditing || !value) && (
        <Styles.Footer>
          <TransparentButton
            isBlueButton
            icon={SaveIcon}
            data-testid="text-editor-save-btn"
            onClick={onSaveContent}
          >
            Save
          </TransparentButton>
          <TransparentButton
            isRedButton
            icon={CancelIcon}
            data-testid="text-editor-cancel-btn"
            disabled={!value}
            onClick={onCancelContentEdition}
          >
            Cancel
          </TransparentButton>
        </Styles.Footer>
      )}
    </Styles.Container>
  );
}
